<template>
  <page-bar title="Profile">
    <v-btn
      icon="mdi-pencil"
      @click="editDialog = true"
      aria-label="Edit profile"
    ></v-btn>
  </page-bar>
  <!-- Settings stay next to their labels: Material 3 caps content at 840px -->
  <v-container v-if="profile" max-width="840">
    <v-row>
      <v-col>
        <profile-card :profile="profile" />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-card>
          <v-list>
            <v-list-subheader>Theme</v-list-subheader>
            <v-list-item>
              <template v-slot:prepend>
                <theme-switch />
              </template>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-card>
          <v-list>
            <v-list-subheader>Week Plan</v-list-subheader>
            <v-list-item title="Week starts on">
              <template v-slot:prepend>
                <v-avatar>
                  <v-icon icon="mdi-calendar-week" />
                </v-avatar>
              </template>
              <template v-slot:append>
                <v-select
                  v-model="weekStartDay"
                  :items="dayOptions"
                  variant="outlined"
                  density="compact"
                  hide-details
                  aria-label="Week starts on"
                  style="max-width: 160px"
                />
              </template>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-card>
          <v-list>
            <v-list-subheader>Recipes</v-list-subheader>
            <!-- Only changes how amounts are shown; stored units stay -->
            <v-list-item title="Units">
              <template v-slot:prepend>
                <v-avatar>
                  <v-icon icon="mdi-scale-balance" />
                </v-avatar>
              </template>
              <template v-slot:append>
                <v-select
                  v-model="unitSystem"
                  :items="unitSystems"
                  variant="outlined"
                  density="compact"
                  hide-details
                  aria-label="Units"
                  style="max-width: 160px"
                />
              </template>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <family-members-card
          :members="familyMembers"
          @generate-invite="generateFamilyInvite"
          @remove-member="removeFamilyMember"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <connected-apps-card
          :tokens="apiTokens"
          :creating="creatingToken"
          :created-token="createdToken"
          :error="tokenError"
          :server-url="mcpUrl"
          @create="createToken"
          @revoke="revokeToken"
          @dismiss="createdToken = ''"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-card>
          <v-list>
            <v-list-subheader>Your data</v-list-subheader>
            <v-list-item
              title="Export my data"
              subtitle="Download a copy as a file"
              :disabled="exporting"
              @click="exportData"
            >
              <template v-slot:prepend>
                <v-avatar>
                  <v-icon icon="mdi-download" />
                </v-avatar>
              </template>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-card>
          <v-list>
            <v-list-subheader>Danger</v-list-subheader>
            <v-list-item
              title="Logout"
              subtitle="Switch to another user"
              @click="logout"
            >
              <template v-slot:prepend>
                <v-avatar>
                  <v-icon icon="mdi-logout" />
                </v-avatar>
              </template>
            </v-list-item>
            <v-list-item
              title="Delete account"
              subtitle="Remove your account and everything in it"
              @click="openDeleteDialog"
            >
              <template v-slot:prepend>
                <v-avatar>
                  <v-icon icon="mdi-account-remove" />
                </v-avatar>
              </template>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
    <delete-account-dialog
      :open="deleteDialog"
      :uses-password="accountClient.usesPassword"
      :deleting="deleting"
      :error="deleteError"
      @confirm="deleteAccount"
      @close="deleteDialog = false"
    />
    <profile-dialog
      :loading="loading"
      :profile="profile"
      :open="editDialog"
      @submit="update"
      @close="editDialog = false"
    />
  </v-container>
</template>

<script lang="ts">
  import ProfileCard from '@/components/profile/profile-card/ProfileCard.vue';
  import ProfileDialog from '@/components/profile/profile-dialog/ProfileDialog.vue';
  import FamilyMembersCard from '@/components/profile/family-members/FamilyMembersCard.vue';
  import ThemeSwitch from '@/components/theme-switch/ThemeSwitch.vue';
  import PageBar from '@/components/infrastructure/page-bar/PageBar.vue';
  import DeleteAccountDialog from '@/components/profile/delete-account/DeleteAccountDialog.vue';
  import type { AccountClientInterface } from '@/service/account/accountClient';
  import ConnectedAppsCard from '@/components/profile/connected-apps/ConnectedAppsCard.vue';
  import type {
    ApiToken,
    ApiTokenClientInterface,
  } from '@/service/apiTokens/apiTokenClient';
  import { cols } from '@/service/vuetifyHelper';
  import { defineComponent, type PropType, ref } from 'vue';
  import type { Profile, ProfilePayload } from '@/service/profile/types';
  import type ProfileClient from '@/service/profile/profileClient';
  import type InviteClient from '@/service/invite/inviteClient';
  import type GroupService from '@/service/group/groupService';
  import { useProfileStore } from '@/stores/profile';
  import { useAuthStore } from '@/stores/auth';
  import { useSettingStore } from '@/stores/settings';
  import { storeToRefs } from 'pinia';
  import { DAYS } from '@/service/weekplan/types';
  import { unitSystems } from '@/service/recipe/units';

  function deleteErrorMessage(e: unknown): string {
    const code = (e as { code?: string }).code ?? '';
    if (['auth/wrong-password', 'auth/invalid-credential'].includes(code)) {
      return 'Wrong password.';
    }
    if (code === 'auth/popup-closed-by-user') {
      return 'Sign in with Google to confirm.';
    }
    return 'Your account could not be deleted. Please try again.';
  }

  export default defineComponent({
    name: 'ProfileView',
    components: {
      PageBar,
      ProfileDialog,
      ThemeSwitch,
      ProfileCard,
      FamilyMembersCard,
      ConnectedAppsCard,
      DeleteAccountDialog,
    },
    props: {
      client: {
        type: Object as PropType<ProfileClient>,
        required: true,
      },
      inviteClient: {
        type: Object as PropType<InviteClient>,
        required: true,
      },
      groupService: {
        type: Object as PropType<GroupService>,
        required: true,
      },
      initialFamilyMembers: {
        type: Array as PropType<Profile[]>,
        default: () => [],
      },
      accountClient: {
        type: Object as PropType<AccountClientInterface>,
        required: true,
      },
      apiTokenClient: {
        type: Object as PropType<ApiTokenClientInterface>,
        required: true,
      },
      initialApiTokens: {
        type: Array as PropType<ApiToken[]>,
        default: () => [],
      },
    },
    setup(props) {
      const authStore = useAuthStore();
      const settingStore = useSettingStore();
      const { weekStartDay, unitSystem } = storeToRefs(settingStore);
      const profileStore = useProfileStore(props.client);
      const profile = profileStore.getProfile();
      const editDialog = ref(false);
      const loading = ref(false);
      const familyMembers = ref<Profile[]>(props.initialFamilyMembers);
      const exporting = ref(false);
      const deleteDialog = ref(false);
      const deleting = ref(false);
      const deleteError = ref('');
      const apiTokens = ref<ApiToken[]>(props.initialApiTokens);
      const creatingToken = ref(false);
      const createdToken = ref('');
      const tokenError = ref('');

      const dayOptions = DAYS.map((d) => ({
        title: d.charAt(0).toUpperCase() + d.slice(1),
        value: d,
      }));

      return {
        cols,
        weekStartDay,
        unitSystem,
        unitSystems,
        dayOptions,
        editDialog,
        loading,
        profile,
        familyMembers,
        async update(payload: ProfilePayload) {
          loading.value = true;
          await profileStore.update(payload);
          loading.value = false;
          editDialog.value = false;
        },
        async generateFamilyInvite(callback: (url: string) => void) {
          const invite = await props.inviteClient.createFamilyInvite();
          const url = `${location.origin}/invite/${invite.id}`;
          callback(url);
        },
        async removeFamilyMember(memberUid: string) {
          await props.groupService.removeMember(memberUid);
          familyMembers.value = familyMembers.value.filter(
            (m) => m.uid !== memberUid,
          );
        },
        logout() {
          authStore.logout();
        },
        apiTokens,
        // Firebase Hosting sends /mcp to the mcp Cloud Function
        mcpUrl: `${location.origin}/mcp`,
        creatingToken,
        createdToken,
        tokenError,
        async createToken(name: string) {
          creatingToken.value = true;
          tokenError.value = '';
          try {
            const { token } = await props.apiTokenClient.create(name);
            createdToken.value = token;
            apiTokens.value = await props.apiTokenClient.list();
          } catch {
            tokenError.value =
              'The token could not be created. Please try again.';
          } finally {
            creatingToken.value = false;
          }
        },
        async revokeToken(id: string) {
          await props.apiTokenClient.revoke(id);
          apiTokens.value = apiTokens.value.filter((token) => token.id !== id);
        },
        exporting,
        // Downloads dinnr-time-2026-10-10.json
        async exportData() {
          exporting.value = true;
          const data = await props.accountClient.exportData();
          const url = URL.createObjectURL(
            new Blob([JSON.stringify(data, null, 2)], {
              type: 'application/json',
            }),
          );
          const link = document.createElement('a');
          link.href = url;
          link.download = `dinnr-time-${data.exportedAt.slice(0, 10)}.json`;
          link.click();
          URL.revokeObjectURL(url);
          exporting.value = false;
        },
        deleteDialog,
        deleting,
        deleteError,
        openDeleteDialog() {
          deleteError.value = '';
          deleteDialog.value = true;
        },
        async deleteAccount(password: string) {
          deleting.value = true;
          deleteError.value = '';
          try {
            await props.accountClient.deleteAccount(password);
            deleteDialog.value = false;
            await authStore.logout();
          } catch (e) {
            deleteError.value = deleteErrorMessage(e);
          } finally {
            deleting.value = false;
          }
        },
      };
    },
  });
</script>

<style scoped></style>
